import { Component, inject, input, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { SubSink } from 'subsink';
import { AuthApiService } from '../../apiCalls/auth-api.service';
import { CustomSnackbarService } from '../../shared/custom-snackbar.service';
import { RoutesConst } from '../../shared/route-const';

interface ForgotPasswordForm {
  email: FormControl<string>;
}

export interface ForgotPasswordFormValue {
  email: string;
  activateAccount: boolean;
}
@Component({
  selector: 'app-forgot-password',
  imports: [MatIconModule, MatButtonModule, MatFormField, MatInputModule, ReactiveFormsModule],
  templateUrl: './forgot-password.html',
  styleUrl: './forgot-password.scss',
})
export class ForgotPassword implements OnDestroy {
  isActive = input.required<boolean>();

  private readonly subs = new SubSink();
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authApiSvc = inject(AuthApiService);
  private readonly customSnackbarSvc = inject(CustomSnackbarService);

  apiErrorMessage = signal<string>('');
  saveState = signal<boolean>(false);
  showSuccessInfo = signal<boolean>(false);
  showEmailNotFoundInfo = signal<boolean>(false);

  form: FormGroup<ForgotPasswordForm> = this.fb.nonNullable.group({
    email: [this.route.snapshot.queryParamMap.get('email') || '', [Validators.required, Validators.email]],
  });

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.resetPassword();
  }

  /**
   * Performs the password reset operation
   */
  private resetPassword(): void {
    const formValue = {
      email: this.form.value.email || '',
      activateAccount: this.isActive(),
    } as ForgotPasswordFormValue;

    this.saveState.set(true);
    this.clearErrorMessage();

    this.customSnackbarSvc.requestSnackbar();

    this.subs.sink = this.authApiSvc.forgotPassword(formValue).subscribe({
      next: () => this.handleSuccess(),
      error: (error) => this.handleError(error),
    });
  }

  /**
   * Handles successful
   */
  private handleSuccess(): void {
    this.clearErrorMessage();
    this.saveState.set(false);
    this.showSuccessInfo.set(true);
    this.customSnackbarSvc.dismiss();
  }

  /**
   * Handles errors
   */
  private handleError(error: any): void {
    const errorMessage = error.message || 'Unknown error occurred';
    if (errorMessage.includes('404')) {
      this.customSnackbarSvc.dismiss();
      this.showEmailNotFoundInfo.set(true);
    } else {
      this.apiErrorMessage.set(`: ${errorMessage}`);
      console.error('Error:', errorMessage);
      this.customSnackbarSvc.errorSnackbar();
    }
    this.saveState.set(false);
  }

  routeToLogin() {
    this.router.navigate([`/${RoutesConst.login}`]);
  }

  /**
   * Clears the API error message
   */
  private clearErrorMessage(): void {
    this.apiErrorMessage.set('');
  }
}
